/**
 * Leviathan Web — Product tax class
 * Quasar Prime LLC
 *
 * Mirrors `ProductTaxClass` in apps/api/prisma/schema.prisma.
 *
 * THIS IS NOT `category`. The two have confusingly similar names and completely
 * different jobs:
 *
 *   category  an INVENTORY grouping — FUEL, ADDITIVE, LUBRICANT, EQUIPMENT,
 *             SERVICE. Decides how the product is organised and reported.
 *   taxClass  a FUEL TYPE — GASOLINE, CLEAR_DIESEL, DYED_DIESEL… Decides which
 *             taxes attach to it.
 *
 * The two enums share exactly one value, OTHER. So a product whose category is
 * FUEL matches NO fuel-specific tax at all until its tax class is set — and
 * before the field existed, every product on every instance was in that state.
 * A diesel load carried no diesel excise, silently.
 *
 * A user will reasonably assume setting one sets the other. Every label and
 * hint in this file exists to stop that assumption costing somebody a return.
 */

export interface TaxClassOption {
  value: string;
  label: string;
  /** What it means in the yard, not in the schema. */
  description: string;
  /** Whether this class typically attracts motor fuel excise. */
  taxed: boolean;
}

export const TAX_CLASS_OPTIONS: TaxClassOption[] = [
  {
    value: 'GASOLINE',
    label: 'Gasoline',
    description: 'All grades — regular, mid-grade, premium, ethanol blends.',
    taxed: true,
  },
  {
    value: 'CLEAR_DIESEL',
    label: 'Clear Diesel (on-road)',
    description: 'Undyed, on-highway diesel. Carries full state and federal excise.',
    taxed: true,
  },
  {
    value: 'DYED_DIESEL',
    label: 'Dyed Diesel (off-road)',
    description:
      'Red-dyed, off-highway. Taxed very differently from clear diesel — getting these two the wrong way round is the most expensive mistake on this list.',
    taxed: true,
  },
  {
    value: 'KEROSENE',
    label: 'Kerosene',
    description: 'K-1 and heating kerosene.',
    taxed: true,
  },
  {
    value: 'JET_FUEL',
    label: 'Jet Fuel / Aviation',
    description: 'Jet A and aviation gasoline.',
    taxed: true,
  },
  {
    value: 'PROPANE',
    label: 'Propane / LPG',
    description: 'Propane and liquefied petroleum gas.',
    taxed: true,
  },
  {
    value: 'DEF',
    label: 'DEF',
    description: 'Diesel exhaust fluid. Not a motor fuel; usually sales tax only.',
    taxed: false,
  },
  {
    value: 'LUBRICANTS',
    label: 'Lubricants',
    description: 'Oils, greases, coolants.',
    taxed: false,
  },
  {
    value: 'OTHER',
    label: 'Other / Not fuel',
    description:
      'Services, equipment, delivery charges — anything no fuel tax should attach to. Choose this deliberately rather than leaving the field empty.',
    taxed: false,
  },
];

export const taxClassLabel = (value: string | null | undefined): string =>
  TAX_CLASS_OPTIONS.find((o) => o.value === value)?.label ?? value ?? 'Not set';

/**
 * OTHER and an unset value produce the same tax outcome — no fuel tax — but
 * they mean opposite things. OTHER is a decision; null is an oversight, and
 * only one of them should raise a warning.
 */
export const isTaxClassMissing = (value: string | null | undefined): boolean =>
  value === null || value === undefined || value === '';

export interface TaxClassSuggestion {
  taxClass: string;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  reason: string;
}
